//src/components/layout/Header

import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSettings } from "../../contexts/SettingsContext";

import ContactModal from "../common/ContactModal";
import getEffectStyles from "../../utils/getEffectStyles";

const BACKEND_URL = "https://imagensdaface.com.br";

const buildLogoUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("blob")) return url;
  return `${BACKEND_URL}/${url.replace(/^\/+/, "")}`;
};

export default function Header() {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, hasRole } = useAuth();
  const { settings, loading } = useSettings();

  const [userOpen, setUserOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const userRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading) return null;

  // Lógica de Cores Fixas (Sempre Modo Claro)
  const bgColor = settings.HEADER_BG_GRADIENT || settings.HEADER_BG_COLOR || "#ffffff";
  const textColor = settings.HEADER_TEXT_COLOR || "#000000";

  return (
    <>
      <header
        className="sticky top-0 z-[100]  mb-30 shadow-xl transition-all duration-300"
        style={{
          background: bgColor,
          color: textColor,
          "--header-text": textColor,
          ...getEffectStyles(settings.HEADER_EFFECT, settings.HEADER_NEON_COLOR),
        }}
      >
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          
          {/* LOGO E NOME DO SITE */}
          <Link to="/home" className="flex items-center gap-3">
            {settings.SHOW_LOGO_TYPE !== "name_only" && settings.SITE_LOGO && (
              <img
                src={buildLogoUrl(settings.SITE_LOGO)}
                alt="Logo"
                style={{
                  height: Number(settings.SITE_LOGO_SIZE) || 42,
                  filter: ["round-shadow", "neon-glow", "border-glow"].includes(settings.SITE_LOGO_EFFECT)
                    ? `drop-shadow(0 0 12px ${settings.SITE_LOGO_SHADOW_COLOR || "rgba(0,0,0,0.1)"})`
                    : "none",
                }}
                className={`site-logo ${settings.SITE_LOGO_EFFECT || ""}`}
              />
            )}
            {settings.SHOW_LOGO_TYPE !== "logo_only" && (
              <span
                className="font-extrabold text-xl tracking-tight"
                style={{ color: "var(--header-text)" }}
              >
                {settings.SITE_NAME || "Imagens da Face"}
              </span>
            )}
          </Link>

          {/* NAVEGAÇÃO */}
          <nav className="flex items-center gap-6 font-medium">
            <Link 
              to="/home" 
              className="hover:opacity-70 transition-opacity"
              style={{ color: "var(--header-text)" }}
            >
              Home
            </Link>

            {isAuthenticated && hasRole?.(["admin", "supervisor"]) && (
              <Link 
                to="/admin" 
                className="hover:opacity-70 transition-opacity"
                style={{ color: "var(--header-text)" }}
              >
                Admin
              </Link>
            )}

            {isAuthenticated && hasRole?.(["professor"]) && (
              <Link 
                to="/professor" 
                className="hover:opacity-70 transition-opacity"
                style={{ color: "var(--header-text)" }}
              >
                Professor
              </Link>
            )}

            {/* DROPDOWN DO USUÁRIO */}
            {isAuthenticated ? (
              <div className="relative" ref={userRef}>
                <button
                  onClick={() => setUserOpen((v) => !v)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all bg-black/5 hover:bg-black/10"
                  style={{ color: "var(--header-text)" }}
                >
                  <span className="text-lg">👤</span>
                  <span className="hidden sm:inline">{user?.name?.split(" ")[0]}</span>
                  <span className={`transition-transform duration-200 ${userOpen ? "rotate-180" : ""}`}>▾</span>
                </button>

                {userOpen && (
                  <div className="absolute right-0 mt-3 w-56 p-2 rounded-2xl shadow-2xl z-50 border bg-white/95 border-gray-200 text-gray-800 backdrop-blur-md animate-in fade-in zoom-in duration-200">
                    
                    <button 
                      onClick={() => { navigate("/perfil"); setUserOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left font-medium hover:bg-gray-100"
                    >
                      <span>👤</span> Meu Perfil
                    </button>

                    <button
                      onClick={() => { logout(); navigate("/login"); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left mt-1 text-red-500 font-medium hover:bg-red-50"
                    >
                      <span>🚪</span> Sair
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="px-5 py-2 rounded-xl font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg transition transform hover:scale-105"
              >
                Login
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* BOTÃO FALE CONOSCO (FIXO) */}
      {settings.CONTACT_BUTTON_ENABLED && (
        <button
          onClick={() => setContactOpen(true)}
          className={`fixed z-50 shadow-2xl font-semibold transition-all px-5 py-3 rounded-full hover:scale-110 active:scale-95 ${
            settings.CONTACT_BUTTON_POSITION === "bottom-left" ? "bottom-6 left-6" : "bottom-6 right-6"
          }`}
          style={{ backgroundColor: settings.CONTACT_BUTTON_COLOR || "#4f46e5", color: "#ffffff" }}
        >
          {settings.CONTACT_BUTTON_ICON && (
            <img src={settings.CONTACT_BUTTON_ICON} alt="" className="inline-block w-5 h-5 mr-2" />
          )}
          {settings.CONTACT_BUTTON_TEXT || "Fale Conosco"}
        </button>
      )}

      <ContactModal
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
        content={settings.CONTACT_MODAL_CONTENT}
      />
    </>
  );
}